import { AlertTriangle } from 'lucide-react'

// Per-branch stock level — horizontal bars around a ZERO baseline.
//
// Why not BarChartH (the shared one): it scales widths off the max and has no
// zero axis, so a negative value renders as a zero-width bar and silently
// VANISHES. A branch whose ledger says −6 would look identical to one at 0 —
// the exact case that most needs to be seen.
//
// ⚠️ A negative level is not a shortage, it is a BOOKKEEPING ERROR. Stock that
// physically cannot be less than nothing reads negative when the ledger is
// missing a row: a เบิกใช้ recorded with no matching รับเพิ่ม, one half of a
// transfer written without its pair, or a count that needed a ปรับ and never
// got one. So it is coloured as an error (rose), not as "low" (amber) — the fix
// is to add the missing row, not to order more.

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')

const POSITIVE = '#2563eb'   // brand-600 — the page's blue
const NEGATIVE = '#e11d48'   // rose-600

export default function BranchLevelBars({ data = [], unit = '', emptyText = 'ไม่มีข้อมูล' }) {
  if (!data.length) {
    return <div className="flex items-center justify-center text-[13px] text-slate-400 h-[180px]">{emptyText}</div>
  }

  // Sorted descending, so the healthiest branch is on top and anything negative
  // sinks to the bottom of the list where the eye ends up.
  const rows = [...data].sort((a, b) => (b.value || 0) - (a.value || 0))

  // The axis always includes 0, so bars stay comparable across items: an item
  // where every branch is positive draws its zero flush left, exactly as a
  // plain bar chart would.
  const lo = Math.min(0, ...rows.map((r) => r.value || 0))
  const hi = Math.max(0, ...rows.map((r) => r.value || 0))
  const span = (hi - lo) || 1
  const zeroPct = ((0 - lo) / span) * 100
  const anyNegative = lo < 0

  return (
    <div className="pt-1">
      <div className="space-y-2">
        {rows.map((r) => {
          const v = Number(r.value) || 0
          const neg = v < 0
          const widthPct = (Math.abs(v) / span) * 100
          const leftPct = neg ? zeroPct - widthPct : zeroPct
          return (
            <div key={r.key} className="flex items-center gap-2.5 text-[12px]">
              <div className="w-28 shrink-0 truncate text-right text-slate-600 flex items-center justify-end gap-1" title={r.label}>
                {neg && <AlertTriangle size={12} className="text-rose-500 shrink-0" />}
                <span className="truncate">{r.label}</span>
              </div>

              <div className="flex-1 min-w-0 relative">
                <div className="h-3.5 rounded bg-slate-100 relative overflow-hidden">
                  <div
                    className="absolute top-0 h-full rounded"
                    style={{
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      minWidth: Math.abs(v) > 0 ? 2 : 0,
                      background: neg ? NEGATIVE : POSITIVE,
                    }}
                  />
                </div>
                {/* The zero line is only drawn when it is actually inside the
                    plot — otherwise it is just a rule down the left edge. */}
                {anyNegative && (
                  <div className="absolute top-0 h-3.5 border-l border-slate-300" style={{ left: `${zeroPct}%` }} />
                )}
              </div>

              <div className={`w-[74px] shrink-0 text-right tabular-nums font-semibold ${
                neg ? 'text-rose-600' : 'text-slate-800'
              }`}>
                {fmt(v)}
                {unit && <span className="font-normal text-slate-400"> {unit}</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
